import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Card, Chip, Icon, Pane, PaneHead, PhoneScreen,
} from '@/components/kit';
import { useShellChrome } from '@/layouts/shellChrome';
import { usePageMeta } from '@/hooks/usePageMeta';
import { allergenOf } from '@/utils/allergyText';
import { captureFile, useMyDay, useNurseTask, useTaskActions, type CourseDose } from './api';
import {
  clock, dayMonth, drName, execTitle, kindLabel, numberWord, shortCondition, todayYmd, useNow,
} from './format';
import { CantModal } from './components/CantModal';
import { NurseSearch } from './components/NurseSearch';
import { FitInput } from './components/FitInput';
import { ReopenModal } from './components/NurseModal';
import { clearDraft, readDraft, writeDraft } from './drafts';
import { NurseMenu } from './components/NurseMenu';

const SITES: Record<string, string[]> = {
  IM: ['Gluteal', 'Deltoid', 'Vastus lateralis', 'Ventrogluteal'],
  SC: ['Abdomen', 'Upper arm', 'Thigh'],
  IV: ['Left forearm', 'Right forearm', 'Left hand', 'Right hand'],
  default: ['Left', 'Right', 'Other — in the note'],
};

/** The clinic-local calendar day of an instant, as `YYYY-MM-DD`. */
function localYmd(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function routeOf(orderText: string | null): keyof typeof SITES {
  const m = (orderText ?? '').match(/\b(IM|SC|IV)\b/i);
  return m ? (m[1].toUpperCase() as keyof typeof SITES) : 'default';
}

/**
 * Nurse · Execute — `g_exec` (an injection, order-locked) and, on its `/cant`
 * route, `g_cant` (the reason, and where it goes).
 *
 * ORDER-LOCKED: drug, dose and route come from the signed order and are shown,
 * never edited — you execute, never prescribe. What she adds is what only
 * she knows: the site, the batch, and a photo or file.
 *
 * The pane is the COURSE: every dose of this order, which one this is, and the
 * next one scheduled. On the Can't route it becomes the three destinations a
 * refusal always goes to.
 */
export function NurseTaskPage({ cant = false }: { cant?: boolean }) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const q = useNurseTask(id);
  const day = useMyDay();
  const { complete, reopen } = useTaskActions();
  const [reopening, setReopening] = useState(false);
  const now = useNow(true);
  /* Site and batch are kept per task until she gives it (drafts.ts) — the
     search, or a detour to My day, never throws them away. */
  const [site, setSiteState] = useState<string | null>(() => readDraft(id).site ?? null);
  const [batchNo, setBatchState] = useState(() => readDraft(id).batchNo ?? '');
  const [searching, setSearching] = useState(false);
  useEffect(() => {
    const d = readDraft(id);
    setSiteState(d.site ?? null);
    setBatchState(d.batchNo ?? '');
  }, [id]);
  const setSite = (v: string) => {
    setSiteState(v);
    writeDraft(id, { site: v, batchNo });
  };
  const setBatchNo = (v: string) => {
    setBatchState(v);
    writeDraft(id, { site: site ?? undefined, batchNo: v });
  };
  const [captured, setCaptured] = useState(0);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const formRef = useRef({ site, batchNo });
  formRef.current = { site, batchNo };
  usePageMeta({ title: cant ? 'Can’t' : 'Execute' });

  const t = q.data;
  const closed = t ? t.state === 'done' || t.state === 'cant' : false;
  const sites = SITES[routeOf(t?.orderText ?? null)];
  const chosenSite = site ?? t?.site ?? sites[0];
  const doc = drName(t?.requestedBy);
  const self = `/nurse/day/task/${id}`;
  const cameBack = (day.data?.tasks ?? []).filter(
    (x) => x.state === 'cant' && x.requestedBy?.id === t?.requestedBy?.id,
  ).length + 1;

  const give = () => {
    if (!t || closed) return;
    const f = formRef.current;
    complete.mutate(
      { id: t.id, outcome: t.kind === 'injection' ? 'given' : 'done', site: f.site ?? chosenSite,
        batchNo: f.batchNo.trim() || undefined },
      {
        onSuccess: () => {
          clearDraft(t.id);
          toast.success(t.kind === 'injection'
            ? 'Given — kept on the task with your name; the observation clock has started'
            : 'Done — kept on the task with your name');
          navigate('/nurse/day');
        },
        onError: (e) => toast.error((e as Error).message || 'That did not go through'),
      },
    );
  };

  const onFile = async (file: File | undefined) => {
    if (!file || !t) return;
    setBusy(true);
    try {
      await captureFile(t.id, file);
      setCaptured((n) => n + 1);
      toast.success('Filed on the patient’s documents');
    } catch (e) {
      toast.error((e as Error).message || 'The file did not upload');
    } finally {
      setBusy(false);
    }
  };

  const doseLine = t?.doseNo && t.doseTotal ? ` · dose ${t.doseNo} of ${t.doseTotal}` : '';
  const verb = t?.kind === 'injection' ? 'Given' : 'Done';
  const drug = (t?.orderText ?? '').split(',')[0].trim();
  const course: CourseDose[] = t?.course ?? [];
  const idx = course.findIndex((d) => d.id === t?.id);
  const visible = idx >= 0 ? course.slice(0, idx + 2) : course;
  const today = todayYmd();

  const allergies = (t?.patient?.allergies ?? []).map((a) => allergenOf(a) || a);
  const completedDay = t?.completedAt ? localYmd(t.completedAt) : null;

  /* THE TASK ITSELF — one column of cards: what the desk draws inside
     .dcontent and what the phone screen scrolls. Defined HERE, above the
     chrome and above the not-found guard, because useShellChrome must run on
     every render — a hook after an early return is a hook that sometimes does
     not run. It is null when there is no task, which is exactly what the
     guard below then renders instead.

     Extracted so both widths render the SAME order text, the same safety
     band and the same verb: a nurse executing from her phone must not be
     shown a different dose from the one on the desk. */
  const taskBody = t ? (
    <>
          {t.orderText ? (
            <div className="card card-acc">
              <p className="t-body" style={{ color: 'var(--brand-600)' }}>
                <span className="w6">Signed order — {doc || 'the doctor'}:</span> {t.orderText}
              </p>
            </div>
          ) : (
            <div className="card card-acc">
              <p className="t-body" style={{ color: 'var(--brand-600)' }}>
                <span className="w6">{t.nurseOnly ? 'Nurse-only visit:' : `${kindLabel(t)}:`}</span>{' '}
                {t.nurseOnly ? 'walk-in, no consultation' : `asked by ${doc || 'the room'}`}
              </p>
            </div>
          )}
  
          {/* One line, as drawn: the station's short forms, three at most, and a
              long one ellipsised (its full name on hover). */}
          <div className="nurse-chiprow" style={{ display: 'flex', gap: 8, margin: '12px 0' }}>
            {(t.patient?.conditions ?? []).slice(0, 3).map((c) => (
              <Chip key={c} kind="outline" small title={c} className="nurse-ellip">{shortCondition(c)}</Chip>
            ))}
            {!t.patient ? null
              : allergies.length === 0 ? <Chip kind="outline" small>allergies not recorded</Chip>
              : t.allergyCheck.state === 'conflict'
                ? <Chip kind="danger" small className="w6">⚠ allergy: {allergies.join(', ')}</Chip>
                : t.orderText ? <Chip kind="success" small>no allergy conflict ✓</Chip>
                : <Chip kind="danger" small>{allergies.join(', ')}</Chip>}
          </div>
  
          <div className="nurse-exec-row" style={{ display: 'flex', gap: 12 }}>
            {/* Site and batch belong to an injection; a dressing or a line of
                teaching has neither. A closed dose shows what was set — no ▾. */}
            {t.kind !== 'injection' ? null : closed ? (
              <div className="card" style={{ flex: 1, margin: 0 }}>
                <p className="t-ovl mut">Site</p>
                <p className="t-heading mt4">{t.site ?? 'not recorded'}</p>
              </div>
            ) : (
              <label className="card" style={{ flex: 1, margin: 0, position: 'relative', cursor: 'pointer' }}>
                <p className="t-ovl mut">Site</p>
                <p className="t-heading mt4">{chosenSite} ▾</p>
                <select aria-label="Site" value={chosenSite} onChange={(e) => setSite(e.target.value)}
                  style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer', width: '100%' }}>
                  {sites.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </label>
            )}
            {t.kind !== 'injection' ? null : (
            <label className="card" style={{ flex: 1, margin: 0, cursor: closed ? 'default' : 'text' }}>
              <p className="t-ovl mut">Batch № <span style={{ textTransform: 'none', letterSpacing: 0 }}>(optional)</span></p>
              {/* Empty, the pencil sits where the text will start; typed, it
                  follows the number after one space — "T-441 ✎". */}
              <p className="t-heading mt4" style={{ overflow: 'hidden', whiteSpace: 'nowrap' }}>
                <FitInput aria-label="Batch number" value={closed ? t.batchNo ?? '' : batchNo} disabled={closed}
                  onChange={(e) => setBatchNo(e.target.value)} maxLength={60} />
                {(closed ? t.batchNo : batchNo) ? ' ' : null}
                {closed ? null : <Icon name="pencil" className="mut" />}
                {closed && !t.batchNo ? <span className="mut">not recorded</span> : null}
              </p>
            </label>
            )}
            <Card
              onClick={() => { if (!busy && !closed) fileRef.current?.click(); }}
              className="nurse-capture"
              style={{ flex: 2, margin: 0, borderStyle: 'dashed', borderColor: 'var(--brand-300)' }}>
              <p className="t-ovl mut">Capture{captured ? ` · ${captured} filed ✓` : ''}</p>
              <p className="t-strong mt4 brand">
                <Icon name="camera" /> {busy ? 'Uploading…' : 'Photo, or attach a file — wound, reaction, a CD the patient brought'}
              </p>
            </Card>
          </div>
  
          <p className="t-sec mut mt12">
            {verb} → kept on the task, with your name and the time.
          </p>
          <div className="card card-ghost mt12">
            <p className="t-sec sec">
              {t.orderText
                ? 'Order-locked: drug, dose and route come from the signed order — you execute, never prescribe.'
                : 'Nothing to prescribe here — what was done, and by whom, is what the record keeps.'}
            </p>
          </div>
          {closed ? (
            <p className="t-sec mut mt12">
              {t.state === 'cant'
                ? 'This task came back with a reason — it is closed.'
                : `${verb} at ${clock(t.completedAt)}${completedDay && completedDay !== today ? ` on ${dayMonth(completedDay)}` : ''} — this task is closed.`}
            </p>
          ) : cant ? (
            <p className="t-sec mut mt12">
              Nothing is performed and nothing is billed until the reason is chosen — the task closes either way, never silently.
            </p>
          ) : null}
    </>
  ) : null;

  useShellChrome(
    {
      raw: true,
      title: t ? (cant ? `Can’t — ${execTitle(t)}` : execTitle(t)) : 'Task',
      /* `g_n3` — execute, order-locked. The same body under a crumb back to
         the day: a phone has no rail to return through. Null when there is no
         task, so the shell falls through to this page's own not-found. */
      phone: taskBody ? (
        <PhoneScreen
          className="nurse-phone"
          crumb="My day"
          title={t ? (cant ? `Can’t — ${execTitle(t)}` : execTitle(t)) : 'Task'}
          onBack={() => navigate('/nurse/day')}
          menu={(close) => <NurseMenu door="day" onClose={close} />}
        >
          {taskBody}
          {/* THE VERB, on the screen itself: a phone has no top bar to carry
              the desk's primary button, and without it the dose cannot be
              given from here at all. */}
          {t ? (
            <div className="nurse-phone-act">
              {closed ? (
                <button type="button" className="btn btn-outline" onClick={() => setReopening(true)}>
                  Reopen — say why
                </button>
              ) : cant ? null : (
                <>
                  <button type="button" className="btn btn-primary nurse-give" onClick={give}>
                    {verb} · {clock(new Date(now), true)}
                  </button>
                  <button type="button" className="lnk amb w6"
                    onClick={() => navigate(`${self}/cant`, { replace: true, state: { reopen: Date.now() } })}>
                    Can’t — reason ▾
                  </button>
                </>
              )}
            </div>
          ) : null}
        </PhoneScreen>
      ) : undefined,
      sub: cant ? (
        <>
          <button type="button" className="lnk" onClick={() => navigate(self)}>‹ {t ? kindLabel(t) : 'Task'}</button>
          {' '}▸ Can’t
        </>
      ) : (
        <>
          <button type="button" className="lnk" onClick={() => navigate('/nurse/day')}>‹ My day</button>
          {' '}▸ {t ? kindLabel(t) : 'Task'}{doseLine}
        </>
      ),
      search: 'Search my day…',
      onSearch: () => setSearching(true),
      /* A closed task's one verb is Reopen — say why. */
      primary: !t ? undefined
        : closed ? { label: 'Reopen — say why', onClick: () => setReopening(true) }
        : cant ? { label: 'Can’t — reason ▾', onClick: () => navigate(`${self}/cant`, { replace: true, state: { reopen: Date.now() } }) }
        : { label: `${verb} · ${clock(new Date(now), true)}`, onClick: give },
      pane: !t ? undefined : cant ? (
        <Pane head={<PaneHead overline="Where it goes · the screen’s own second half" title="Three destinations"
          sub="always the same three, for every reason" />}>
          <div className="card">
            <p className="t-body">
              1 · {doc || 'The doctor'} is <span className="w6">notified</span> — “{cameBack} task{cameBack === 1 ? '' : 's'} came back”
            </p>
          </div>
          <div className="card">
            <p className="t-body">2 · An <span className="w6">attributed line</span> on the task — your name, the reason, the time</p>
          </div>
          <div className="card"><p className="t-body">3 · The task <span className="w6">closes</span> — never silently</p></div>
          <p className="t-cap mut center mt12">“I told the doctor” stops being a corridor event</p>
        </Pane>
      ) : (
        <Pane
          head={
            <PaneHead
              overline={course.length ? 'The course · the screen’s own second half' : 'The task · the screen’s own second half'}
              title={course.length ? drug || kindLabel(t) : kindLabel(t)}
              sub={course.length && t.doseNo && t.doseTotal
                ? `${numberWord(t.doseTotal)} doses · this is dose ${t.doseNo}`
                : `${doc || 'booked at the desk'}${t.orderSignedAt ? ` · signed ${clock(t.orderSignedAt)}` : ''}`}
            />
          }
          foot={closed ? undefined : (
            <button type="button" className="btn btn-outline grow"
              style={{ minHeight: 44, borderColor: 'var(--warning-600)', color: 'var(--warning-600)' }}
              onClick={() => navigate(`${self}/cant`)}>
              Can’t — reason ▾
            </button>
          )}
        >
          {course.length ? (
            <>
              <div className="card">
                {visible.map((d, i) => {
                  const isNow = d.id === t.id;
                  const day = d.scheduledFor ?? d.createdAt.slice(0, 10);
                  /* "today" is a date, not "the dose on this screen" — a past
                     dose opened later keeps its own day. */
                  const when = day === today ? 'today' : dayMonth(day);
                  const cls = isNow ? 't-strong grow' : d.state === 'open' ? 't-body mut grow' : 't-body grow';
                  return (
                    <div key={d.id} className={i ? 'flex mt8' : 'flex'} style={{ gap: 8, alignItems: 'center' }}>
                      <p className={cls}>Dose {d.doseNo} · {when}</p>
                      {d.state === 'done' ? <Chip kind="success" small>given</Chip>
                        : d.state === 'cant' ? <Chip kind="warn" small>missed</Chip>
                        : isNow && day <= today ? <Chip kind="warn" small>now</Chip>
                        : (d.scheduledFor ?? '') < today ? <Chip kind="warn" small>overdue</Chip>
                        : <Chip kind="outline" small>scheduled</Chip>}
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <div className="card">
              <p className="t-body">
                {t.nurseOnly ? 'A nurse-only visit — walk-in, no consultation.' : 'One task, no course.'}
              </p>
            </div>
          )}
        </Pane>
      ),
    },
    [id, cant, t?.state, t?.id, course.length, closed, Math.floor(now / 60000), cameBack],
  );

  if (!t) {
    return (
      <div className="maie desk nurse-root"><div className="dcontent">
        {q.isError ? (
          <div className="emptystate">
            <p className="es-t">This task is not on your day.</p>
            <p className="t-sec mut">It may have been handed over or closed by a colleague.</p>
            <p className="mt12">
              <button type="button" className="dchip dchip-outline" onClick={() => navigate('/nurse/day')}>Back to My day</button>
            </p>
          </div>
        ) : <p className="t-sec mut">Loading the task…</p>}
      </div></div>
    );
  }


  return (
    <div className="maie desk nurse-root">
      <input ref={fileRef} type="file" accept="image/*,application/pdf,.dcm" hidden
        onChange={(e) => { void onFile(e.target.files?.[0]); e.target.value = ''; }} />
      <div className="dcontent">
        {taskBody}
      </div>

      <NurseSearch open={searching} onClose={() => setSearching(false)} />
      <ReopenModal open={reopening} onClose={() => setReopening(false)} title={execTitle(t)} busy={reopen.isPending}
        onReopen={(reason) => reopen.mutate({ id: t.id, reason }, {
          onSuccess: () => {
            toast.success('Reopened — your reason is on the record beside the line that closed it');
            setReopening(false);
            void q.refetch();
          },
          onError: (e) => toast.error((e as Error).message || 'Could not reopen it'),
        })} />
      {cant && !closed ? (
        <CantModal task={t} open onClose={() => navigate(self, { replace: true })} onDone={() => navigate('/nurse/day')} />
      ) : null}
    </div>
  );
}
